import { z } from 'zod';
/**
 * Tenant white-label configuration.
 *
 * Companion to `docs/architecture/notification-sender-identity-and-providers.md`.
 * The storage `master-ui-specification.md` §33 already assumed, consumed by the
 * notification renderer today and by the web theme next — ONE row, because two
 * sources of brand truth drift and the visible failure is a logo correct in the
 * product and wrong in the email.
 */
/** `#rgb` or `#rrggbb`. */
export const hexColourSchema = z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Expected a hex colour such as #1565c0');
/** Relative luminance, per WCAG 2.1. */
function luminance(hex) {
    const h = hex.replace('#', '');
    const full = h.length === 3
        ? h
            .split('')
            .map((c) => c + c)
            .join('')
        : h;
    const channel = (i) => {
        const v = parseInt(full.slice(i * 2, i * 2 + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}
/** WCAG contrast ratio between two hex colours, 1–21. */
export function contrastRatio(a, b) {
    const [x, y] = [luminance(a), luminance(b)];
    const [hi, lo] = x > y ? [x, y] : [y, x];
    return (hi + 0.05) / (lo + 0.05);
}
/**
 * The accent is used as a button background with white text, so it must clear AA
 * against white.
 *
 * Validated at CONFIGURATION time rather than at render time, exactly as the UI
 * specification requires: the renderer then trusts the token, and a tenant learns
 * their colour is unreadable while they are choosing it rather than after their
 * members cannot read an alert.
 */
export const MIN_ACCENT_CONTRAST = 4.5;
export function accentIsReadable(accent) {
    return contrastRatio(accent, '#ffffff') >= MIN_ACCENT_CONTRAST;
}
/**
 * A curated set of accent colours, every one of which clears AA on white.
 *
 * Offered because the alternative is a free-text hex field and a rejection: the
 * contrast rule is real and non-negotiable (accents are used as button backgrounds
 * with white text), so a tenant picking by eye discovers it by being refused. A
 * palette moves that from "learn by rejection" to "safe by construction".
 *
 * **Not a restriction.** The hex field stays, and any colour clearing the same bar
 * is still accepted. This is a shortcut to a good answer, not a fence.
 *
 * Every entry is asserted against `accentIsReadable` by a test rather than trusted,
 * because these were chosen by measuring — a warm orange looks like it belongs here
 * and does not survive the check. Deep orange, orange and amber at their usual
 * shades all fail on white, which is why the warm end of this list is darker than
 * a designer would first reach for.
 */
export const ACCENT_PALETTE = [
    { name: 'Blue', hex: '#1565c0' },
    { name: 'Deep blue', hex: '#01579b' },
    { name: 'Indigo', hex: '#283593' },
    { name: 'Deep purple', hex: '#4527a0' },
    { name: 'Purple', hex: '#6a1b9a' },
    { name: 'Magenta', hex: '#880e4f' },
    { name: 'Pink', hex: '#ad1457' },
    { name: 'Red', hex: '#b71c1c' },
    { name: 'Burnt orange', hex: '#bf360c' },
    { name: 'Brown', hex: '#5d4037' },
    { name: 'Olive', hex: '#827717' },
    { name: 'Green', hex: '#2e7d32' },
    { name: 'Forest', hex: '#1b5e20' },
    { name: 'Teal', hex: '#00695c' },
    { name: 'Cyan', hex: '#00838f' },
    { name: 'Slate', hex: '#455a64' },
    { name: 'Charcoal', hex: '#37474f' },
    { name: 'Graphite', hex: '#616161' },
];
/**
 * What a font-family value may contain.
 *
 * Named and exported so the schema and the preset list are checked against ONE
 * definition. The value is interpolated into a CSS declaration, so `;` `{` `}` and
 * friends are refused rather than trusted: a family string that could terminate the
 * declaration would let a tenant append arbitrary rules, and a CSS rule can
 * exfiltrate through a `url()`.
 */
export const FONT_FAMILY_PATTERN = /^[\w\s,'".-]+$/;
/**
 * Ready-made font stacks, offered because nobody remembers font names.
 *
 * Every one is present on the operating systems this product runs on, and **none of
 * them fetches anything**. A web-font preset would mean a request to a third party
 * on every page load: a new external dependency, a privacy question nobody asked
 * for, and unstyled text while it loads. A tenant who licenses a brand font can
 * still type its name — the value is prepended to the platform stack, so an
 * unavailable one falls through to something readable rather than breaking the page.
 *
 * The `stack` is what gets stored, and it deliberately carries no generic family
 * (`sans-serif` and friends): the theme appends the platform stack after it, and a
 * generic in the middle would stop that fallback ever being reached.
 */
export const FONT_FAMILY_PRESETS = [
    { name: 'Neutral sans', stack: '"Helvetica Neue", Helvetica, Arial', note: 'Plain and familiar' },
    { name: 'Humanist sans', stack: '"Segoe UI", Roboto, Arial', note: 'Warmer, slightly rounded' },
    { name: 'Wide sans', stack: 'Verdana, Geneva', note: 'Very legible on small screens' },
    { name: 'Compact sans', stack: 'Tahoma, Geneva', note: 'Narrower, fits more in' },
    { name: 'Friendly sans', stack: '"Trebuchet MS", Tahoma', note: 'Informal' },
    { name: 'Editorial serif', stack: 'Georgia, "Times New Roman"', note: 'Traditional, print-like' },
    { name: 'Classical serif', stack: '"Palatino Linotype", Palatino, Georgia', note: 'Formal' },
    {
        name: 'Technical mono',
        stack: '"SF Mono", Menlo, Consolas, "Courier New"',
        note: 'Engineering feel',
    },
];
export const densitySchema = z.enum(['COMFORTABLE', 'COMPACT']);
/** The tenant's STARTING point. A person's own choice still wins. */
export const colourModeSchema = z.enum(['SYSTEM', 'LIGHT', 'DARK']);
/**
 * A named choice, not a nav colour.
 *
 * With `ACCENT` the foreground is DERIVED — whichever of white or near-black
 * clears AA against the tenant's primary. Offering a background colour would
 * oblige the tenant to also pick readable text against it, and most would not.
 */
export const navStyleSchema = z.enum(['PAPER', 'ACCENT']);
/** Near-black rather than pure black: softer against a saturated accent. */
export const NAV_FOREGROUND_DARK = '#101418';
export const NAV_FOREGROUND_LIGHT = '#ffffff';
/** Whichever of white or near-black is more readable on `background`. */
export function readableOn(background) {
    return contrastRatio(background, NAV_FOREGROUND_LIGHT) >=
        contrastRatio(background, NAV_FOREGROUND_DARK)
        ? NAV_FOREGROUND_LIGHT
        : NAV_FOREGROUND_DARK;
}
export const tenantBrandingSchema = z.object({
    displayName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    accent: z.string().nullable(),
    secondaryAccent: z.string().nullable(),
    fontFamily: z.string().nullable(),
    cornerRadius: z.number().int().nullable(),
    density: densitySchema.nullable(),
    defaultColourMode: colourModeSchema.nullable(),
    navStyle: navStyleSchema.nullable(),
    faviconUrl: z.string().nullable(),
    /**
     * The envelope sender this tenant has chosen, or null for the derived one.
     *
     * Only settable when its domain has a live `TenantSendingDomain` row, which only a
     * platform administrator can create (§5, D9).
     */
    fromEmail: z.string().nullable(),
    /**
     * The domains this tenant may send as, live only.
     *
     * Sent with the branding so the form can SAY which addresses are permitted rather
     * than letting somebody discover it by being rejected. Empty is the normal state
     * and the form explains it instead of showing a dead field.
     */
    sendingDomains: z.array(z.string()),
    replyToEmail: z.string().nullable(),
    supportEmail: z.string().nullable(),
    supportPhone: z.string().nullable(),
    footerText: z.string().nullable(),
    /**
     * The envelope sender actually in use, resolved and shown in full.
     *
     * `fromEmail` when set and authorised, otherwise the platform address. Shown
     * because the rule is otherwise invisible: a tenant with no authorised domain
     * needs to see WHAT is being sent on their behalf, not just that they cannot
     * change it.
     */
    effectiveFrom: z.string(),
    /** True when this tenant has configured anything at all. */
    configured: z.boolean(),
    /**
     * The fields this tenant may actually SET.
     *
     * Sent so the form can disable the rest and say why, rather than offering a
     * control whose only outcome is a 400 — the same reason `sendingDomains` is
     * sent rather than discovered by being rejected.
     *
     * Every field for a tenant with no white-label partner, which is every tenant
     * on the platform today. Under a partner it narrows: a company keeps its name,
     * sender and colours; a reseller keeps colours alone.
     */
    settableFields: z.array(z.string()),
    /**
     * The partner whose brand sits above this tenant, or null for a direct
     * customer. Named so the form can say WHOSE brand it is deferring to instead
     * of reporting an anonymous restriction.
     */
    brandOwner: z.string().nullable(),
});
/**
 * A white-label partner's own branding — the top of the chain.
 *
 * The same fields, minus everything that is a TENANT's answer: there is no
 * `sendingDomains` (a partner sends nothing itself), no `effectiveFrom`, and no
 * `settableFields`, because a partner may set all of them. Its own schema rather
 * than a reuse with three fields made optional, so a field added to one is a
 * decision about the other rather than an accident.
 */
export const partnerBrandingSchema = z.object({
    whiteLabelPartnerId: z.string().uuid(),
    displayName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    faviconUrl: z.string().nullable(),
    accent: z.string().nullable(),
    secondaryAccent: z.string().nullable(),
    fontFamily: z.string().nullable(),
    cornerRadius: z.number().int().nullable(),
    density: densitySchema.nullable(),
    defaultColourMode: colourModeSchema.nullable(),
    navStyle: navStyleSchema.nullable(),
    fromEmail: z.string().nullable(),
    replyToEmail: z.string().nullable(),
    supportEmail: z.string().nullable(),
    supportPhone: z.string().nullable(),
    footerText: z.string().nullable(),
    /** True when the partner has configured anything at all. */
    configured: z.boolean(),
});
export const updateTenantBrandingSchema = z
    .object({
    displayName: z.string().trim().min(1).max(60).nullable().optional(),
    logoUrl: z.string().url().max(500).nullable().optional(),
    accent: hexColourSchema.nullable().optional(),
    secondaryAccent: hexColourSchema.nullable().optional(),
    /**
     * A font-family list, constrained to characters a family name can contain.
     *
     * This value is interpolated into a CSS declaration, so `;` `{` `}` and
     * friends are refused rather than trusted: a family string that could
     * terminate the declaration would let a tenant append arbitrary rules, and a
     * CSS rule can exfiltrate through a `url()`. Letters, digits, spaces,
     * hyphens, underscores, commas, dots and quotes are enough to name any real
     * font.
     */
    fontFamily: z
        .string()
        .trim()
        .max(120)
        .regex(FONT_FAMILY_PATTERN, 'Use only font names, quotes and commas.')
        .nullable()
        .optional(),
    /** Bounded by the UI specification, not by taste. */
    cornerRadius: z.number().int().min(0).max(12).nullable().optional(),
    density: densitySchema.nullable().optional(),
    defaultColourMode: colourModeSchema.nullable().optional(),
    navStyle: navStyleSchema.nullable().optional(),
    faviconUrl: z.string().url().max(500).nullable().optional(),
    /**
     * The envelope sender. Refused unless its domain is authorised (§5, D9).
     *
     * Shape is checked here; AUTHORISATION is checked by the service, because it is
     * a database question and a zod schema has no business asking one.
     */
    fromEmail: z.string().email().max(320).nullable().optional(),
    replyToEmail: z.string().email().max(320).nullable().optional(),
    supportEmail: z.string().email().max(320).nullable().optional(),
    supportPhone: z.string().trim().max(40).nullable().optional(),
    footerText: z.string().trim().max(300).nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    // Both accents are used as a button background with white text, so both must
    // clear AA. Checking only the primary was an oversight: the secondary is
    // applied the same way and would fail the same people.
    for (const key of ['accent', 'secondaryAccent']) {
        const value = v[key];
        if (value != null && !accentIsReadable(value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: 'That colour is too light for white text. Choose a darker one so buttons and headings stay readable.',
            });
        }
    }
    // Only https: a logo over http is a mixed-content warning in the product and a
    // blocked image in most mail clients, which reads as a broken brand.
    for (const key of ['logoUrl', 'faviconUrl']) {
        const value = v[key];
        if (value != null && !value.startsWith('https://')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: 'It must be served over https.',
            });
        }
    }
    /*
     * A branded nav needs an accent to fill itself with.
     *
     * Refusing the combination beats rendering a nav in the PLATFORM accent and
     * letting the tenant conclude their colour was ignored.
     */
    if (v.navStyle === 'ACCENT' && v.accent === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['navStyle'],
            message: 'Set an accent colour before filling the navigation with it.',
        });
    }
});
/**
 * A partner editing its own brand.
 *
 * Reuses the tenant update schema's field VALIDATION rather than restating it —
 * the font-family character rule exists because that value is interpolated into
 * a CSS declaration, and a second copy of it is a second place to get a CSS
 * injection wrong.
 *
 * `sendingDomains` has no equivalent here and `fromEmail` is deliberately still
 * validated the same way: a partner's `fromEmail` is inherited by every company
 * beneath it, so the allow-list applies to whichever tenant actually sends —
 * checked on the tenant path, where the domain rows live.
 */
export const updatePartnerBrandingSchema = updateTenantBrandingSchema;
export const WHITE_LABEL_FEATURE = 'WHITE_LABEL';
/**
 * The effective theme, readable by EVERY authenticated role.
 *
 * Separate from the branding configuration route on purpose, and the differences
 * are the point:
 *
 *  - **Every role**, because a viewer sees the same product as an administrator
 *    and a theme that only applied to admins would be a strange kind of branding.
 *  - **Never 403.** A tenant without `WHITE_LABEL` gets platform defaults rather
 *    than an error, because the application must still render. This route is
 *    therefore where the entitlement is ENFORCED for display: revoking white-label
 *    returns everyone to defaults on their next load.
 *  - **No support details or reply-to.** The theme is what the browser needs to
 *    paint; contact fields belong to the configuration screen and to the renderer.
 */
export const tenantThemeSchema = z.object({
    /** Product wordmark. Null means the platform name. */
    displayName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    accent: z.string().nullable(),
    secondaryAccent: z.string().nullable(),
    fontFamily: z.string().nullable(),
    cornerRadius: z.number().int().nullable(),
    density: densitySchema.nullable(),
    defaultColourMode: colourModeSchema.nullable(),
    navStyle: navStyleSchema.nullable(),
    faviconUrl: z.string().nullable(),
    /** False when the tenant is unentitled or has configured nothing. */
    branded: z.boolean(),
});
/**
 * The brand an ANONYMOUS visitor sees, resolved from the hostname alone.
 *
 * Deliberately the SAME shape as `tenantThemeSchema` rather than a second one.
 * It already contains exactly the presentational fields and nothing else — no
 * `fromEmail`, no support contacts, no identifiers — and two shapes that must
 * stay in step eventually diverge, at which point the public one is the
 * dangerous half.
 *
 * **What this route must never become:** it is served to anybody who can reach
 * the login page. It may say what a product looks like; it may not say who the
 * customers are. A test greps the response for identifiers.
 */
export const publicThemeSchema = tenantThemeSchema;
export const publicThemeQuerySchema = z
    .object({
    /**
     * The hostname being viewed, e.g. `morgan.oip.com`.
     *
     * Sent by the client as `window.location.hostname` rather than read from
     * the `Host` header: behind a proxy that header is proxy-supplied, and a
     * route whose behaviour depends on a header a proxy may or may not forward
     * breaks differently in every environment.
     */
    host: z.string().trim().min(1).max(255),
})
    .strict();
// ── the sending-domain allow-list ───────────────────────────────────────────
/**
 * A hostname, not a URL and not an address.
 *
 * Deliberately permissive about TLDs and length and strict about SHAPE: at least two
 * labels, alphanumerics and hyphens only, no leading or trailing hyphen. A
 * registry-accurate check would reject valid internal domains and go stale, and it is
 * not what makes this safe — the email provider having verified the domain is.
 */
export const sendingDomainNameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(253)
    .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/, 'Enter a domain such as fleet.example.com — not an email address or a URL.');
export const sendingDomainSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    /** Denormalised for the platform list, which spans tenants. */
    tenantName: z.string(),
    domain: z.string(),
    note: z.string().nullable(),
    /** Null while live. Revoked rows are kept — see the model comment. */
    revokedAt: z.string().nullable(),
    revokedReason: z.string().nullable(),
    authorisedAt: z.string(),
    /**
     * Whether a tenant's stored `fromEmail` currently depends on this row.
     *
     * Shown before a revocation so the consequence is visible: revoking it clears
     * their sender rather than leaving mail to fail SPF quietly.
     */
    inUse: z.boolean(),
    /**
     * WHO authorised it — a platform administrator, or the tenant's white-label
     * partner (design D11b).
     *
     * A partner may authorise a domain for one of its own companies, and since
     * 30 August 2026 may also REVOKE one a platform administrator authorised
     * (Q13, answered permissively). Refusal is therefore not the control;
     * visibility is. A reader cannot judge a row without knowing whose decision
     * it was, and the exposure has to be listable rather than discovered.
     *
     * Null for rows written before this was recorded, and for an authoriser whose
     * account has since been deleted — visibly unresolved beats inventing a name.
     */
    authorisedBy: z
        .object({ userId: z.string().uuid().nullable(), label: z.string(), partner: z.boolean() })
        .nullable(),
    /**
     * Whether this company's mail leaves through its OWN server or the platform's.
     *
     * The second half of D11b's visibility condition, and the combination worth
     * being able to list: a partner-authorised domain riding the PLATFORM's sender
     * spends deliverability reputation shared with every other tenant. Not refused
     * — that belongs in the partner contract — but the first anybody hears of it
     * should be a screen rather than a deliverability report.
     */
    sendsVia: z.enum(['OWN_SERVER', 'PLATFORM']),
});
export const sendingDomainsResponseSchema = z.object({
    items: z.array(sendingDomainSchema),
});
export const authoriseSendingDomainSchema = z
    .object({
    tenantId: z.string().uuid(),
    domain: sendingDomainNameSchema,
    /**
     * What was done provider-side, for whoever reads this in six months.
     *
     * Optional rather than required: making it mandatory produces "verified" typed a
     * thousand times, which is worse than an empty field because it looks like
     * information.
     */
    note: z.string().trim().max(300).nullable().optional(),
})
    .strict();
export const revokeSendingDomainSchema = z
    .object({
    /**
     * Required, unlike the note on authorising.
     *
     * Revoking stops a tenant sending as their own domain and silently rewrites
     * their sender. An unexplained one is an outage with no record — the same
     * reasoning as a channel suspension (D20).
     */
    reason: z.string().trim().min(3).max(300),
})
    .strict();
//# sourceMappingURL=branding.js.map